import companiesData from '../data/companies.json' with { type: 'json' };
import type { Company } from '@smartphone-tycoon/shared-core';
import { GAME_CONFIG } from '../constants/gameConfig';

// Base monthly sales for different market positions
const BASE_MONTHLY_SALES = {
  HIGH: 12000,
  MEDIUM: 8000,
  LOW: 5000
};

// Cache for consistent sales numbers
const salesCache: { [key: string]: number } = {};

function calculateUnitsSold(
  productId: string,
  marketAppeal: number,
  companyMarketShare: number,
  gameDate: { year: number; month: number }
): number {
  // Create a unique key for this product and month
  const cacheKey = `${productId}-${gameDate.year}-${gameDate.month}`;

  // If we already calculated sales for this month, return cached value
  if (salesCache[cacheKey]) {
    return salesCache[cacheKey];
  }

  // Determine base monthly sales based on market share
  let baseSales = BASE_MONTHLY_SALES.MEDIUM;
  if (companyMarketShare >= 30) {
    baseSales = BASE_MONTHLY_SALES.HIGH;
  } else if (companyMarketShare < 20) {
    baseSales = BASE_MONTHLY_SALES.LOW;
  }

  // Calculate sales based on market appeal and market share
  const monthlySales = Math.round(
    baseSales * (marketAppeal / 100) * (1 + companyMarketShare / 100)
  );

  // Calculate total sales up to this month
  const monthsInBusiness = (gameDate.year - GAME_CONFIG.STARTING_YEAR) * 12 + gameDate.month;
  const totalSales = monthlySales * monthsInBusiness;

  // Cache the result
  salesCache[cacheKey] = totalSales;

  return totalSales;
}

// Function overloads for sync/async behavior
export function getCompanies(
  gameDate: { year: number },
  playerCompanyInfo?: { name: string; ceoName: string }
): Company[];
export function getCompanies(
  gameDate: { year: number },
  playerCompanyInfo: { name: string; ceoName: string } | undefined,
  includeDbProducts: true
): Promise<Company[]>;
export function getCompanies(
  gameDate: { year: number },
  playerCompanyInfo?: { name: string; ceoName: string },
  includeDbProducts?: boolean
): Company[] | Promise<Company[]> {
  // Base company data
  const baseCompanies = [
    // Player's company is always first
    {
      id: 'player',
      name: playerCompanyInfo?.name || 'Your Company',
      ceoName: playerCompanyInfo?.ceoName || 'You',
      founded: {
        day: 1,
        month: 1,
        year: gameDate.year
      },
      productCount: 0,
      isPlayer: true
    },
    // Load competitor companies from JSON
    ...companiesData.companies.map((company: Record<string, unknown>) => ({
      id: company.id,
      name: company.name,
      ceoName: company.ceo.name,
      founded: company.founded,
      productCount: 0,
      isPlayer: false
    }))
  ];

  // If not including DB products, use static JSON counts
  if (!includeDbProducts) {
    const companyProducts = new Map<string, number>();
    
    const products = companiesData.products as Record<string, unknown>;
    Object.values(products).forEach((product: Record<string, unknown>) => {
      const count = companyProducts.get(product.companyId) || 0;
      companyProducts.set(product.companyId, count + 1);
    });
    
    baseCompanies.forEach(company => {
      if (!company.isPlayer) {
        company.productCount = companyProducts.get(company.id) || 0;
      }
    });
    
    return baseCompanies;
  }

  // Async mode - fetch from database
  return (async () => {
    try {
      const { ProductManager } = await import('../services/productManager');
      const result = await ProductManager.getAllProducts();
      
      if (result.success && result.data) {
        // Count products for each company
        const companyProducts = new Map<string, number>();
        
        result.data.forEach(product => {
          // Count all products that have been released (released or discontinued)
          if ((product.status === 'released' || product.status === 'discontinued') &&
              (product.status === 'released' || product.metadata?.releaseDate)) {
            const companyId = product.metadata?.companyId || 'player';
            const count = companyProducts.get(companyId) || 0;
            companyProducts.set(companyId, count + 1);
          }
        });
        
        // Update product counts
        baseCompanies.forEach(company => {
          company.productCount = companyProducts.get(company.id) || 0;
        });
      }
    } catch (error) {
      console.error('Error fetching products from database:', error);
    }
    
    return baseCompanies;
  })();
}

export function getCompanyDetails(companyId: string) {
  return companiesData.companies.find(company => company.id === companyId);
}

export function getCompanyProducts(
  companyId: string,
  gameDate?: { year: number; month: number }
) {
  const company = companiesData.companies.find((c: Record<string, unknown>) => c.id === companyId);
  const productsData = companiesData.products as Record<string, unknown>;
  const products = Object.values(productsData)
    .filter((product: Record<string, unknown>) => product.companyId === companyId);

  if (!gameDate) {
    return products;
  }

  // Add calculated units sold if game date is provided
  return products.map((product: Record<string, unknown>) => ({
    ...product,
    unitsSold: calculateUnitsSold(
      product.id,
      product.marketAppeal,
      company?.metrics?.marketShare || 0,
      gameDate
    )
  }));
}

export function getCompanySuppliers(companyId: string) {
  const company = companiesData.companies.find(c => c.id === companyId);
  if (!company) return [];
  
  return company.relationships.suppliers.map(supplierId => 
    companiesData.suppliers[supplierId as keyof typeof companiesData.suppliers]
  );
}

export function getCompanyCompetitors(companyId: string) {
  const company = companiesData.companies.find(c => c.id === companyId);
  if (!company) return [];

  return company.relationships.competitors.map(competitorId =>
    companiesData.companies.find(c => c.id === competitorId)
  );
}